<template>
  <Layout>
    <div class="cd-page">
      <DashboardHeader
        :filters="filters"
        :filter-options="filterOptions"
        :is-loading="isLoading"
        :last-update="lastUpdate"
        @change-filters="handleChangeFilters"
        @refresh="handleRefresh"
        @open-reports="openReportsModal"
      />

      <InsightsTicker :insights="insights" />

      <div v-if="isFirstLoad" class="cd-skeleton">
        <div class="cd-sk-hero"></div>
        <div class="cd-sk-row">
          <div class="cd-sk-card"></div>
          <div class="cd-sk-card"></div>
          <div class="cd-sk-card"></div>
          <div class="cd-sk-card"></div>
        </div>
        <div class="cd-sk-chart"></div>
        <div class="cd-sk-tag-grid">
          <div class="cd-sk-tag" v-for="n in 4" :key="n"></div>
        </div>
        <div class="cd-sk-heatmap"></div>
        <div class="cd-sk-field"></div>
        <div class="cd-sk-funnel"></div>
        <div class="cd-sk-flow"></div>
        <div class="cd-sk-ops">
          <div class="cd-sk-ops-card"></div>
          <div class="cd-sk-ops-card"></div>
        </div>
      </div>

      <template v-else>
        <div class="cd-hero-row">
          <HeroPulseCard
            :inside-now="overview ? overview.inside_now : 0"
            :velocity-per-min="overview ? overview.velocity_per_min : null"
            :attendance="overview ? overview.attendance : null"
            :inside-now-mode="overview ? overview.inside_now_mode : 'last_event'"
            :dwell-minutes="overview && overview.dwell_minutes ? overview.dwell_minutes : 15"
            :last-read-at="overview ? overview.last_read_at : null"
          />

          <LiveCheckinFeed
            embedded
            :live-feed="liveFeed"
            :live-feed-connected="liveFeedConnected"
            :live-feed-paused="liveFeedPaused"
            :show-checkout="showCheckout"
            @toggle-pause="handleTogglePause"
            @clear="handleClearLive"
          />
        </div>

        <div class="cd-spacer"></div>

        <KpiSparkRow
          :velocity-per-min="overview ? overview.velocity_per_min : null"
          :velocity-window-min="overview ? overview.velocity_window_min : null"
          :last-read-at="overview ? overview.last_read_at : null"
          :avg-interval-seconds="overview ? overview.avg_interval_seconds : null"
          :denied24h="overview ? overview.denied_24h : null"
          :peak="overview ? overview.peak : null"
          :timeline="timeline"
          :total-check-ins="overview && overview.attendance ? overview.attendance.total_attended : 0"
        />

        <div class="cd-spacer"></div>

        <RiverTimeline
          :timeline="timeline"
          :bucket="bucket"
          :peak-hour="overview && overview.peak ? overview.peak.hour : null"
          :show-checkout="showCheckout"
          @change-bucket="handleBucketChange"
        />

        <div class="cd-spacer"></div>

        <TagFlowGrid
          :tags="byTag"
          :show-checkout="showCheckout"
          :window-minutes="byTagWindowMinutes"
          @select-tag="handleSelectTag"
          @change-window="handleTagWindowChange"
        />

        <div class="cd-spacer"></div>

        <TopLocations
          :locations="topLocations"
          @select-location="handleSelectLocation"
        />

        <div class="cd-spacer"></div>

        <TagHourHeatmap :heatmap="heatmap" />

        <div class="cd-spacer"></div>

        <FieldAnalysisCard
          :primary="fieldAnalysis.primary"
          :secondary="fieldAnalysis.secondary"
          :data="fieldAnalysis.data"
          :is-loading="fieldAnalysis.isLoading"
          :fields="filterOptions.fields || []"
          @change-primary="handlePrimaryFieldChange"
          @change-secondary="handleSecondaryFieldChange"
        />

        <div class="cd-spacer"></div>

        <TransitionFlow :flow="flow" />

        <div class="cd-spacer"></div>

        <div class="cd-ops-grid">
          <OperatorPerformance
            :operators="operators"
            @select-operator="handleSelectOperator"
          />
          <AnomaliesPanel :anomalies="anomalies" />
        </div>

        <div v-if="error" class="cd-error">
          <i class="bx bx-error-circle"></i>
          {{ error }}
        </div>
      </template>

      <TagAnalyticsDrawer
        :open="tagDetail !== null"
        :detail="tagDetail"
        :is-loading="tagDetailLoading"
        :bucket="tagDetailBucket"
        :is-fair="isFairMode"
        :available-fields="filterOptions.fields || []"
        :field-selected="tagDetailField"
        :field-secondary="tagDetailFieldSecondary"
        :field-data="tagDetailFieldData"
        :field-loading="tagDetailFieldLoading"
        @close="closeTagDetail"
        @change-bucket="setTagDetailBucket"
        @change-field="setTagDetailField"
        @change-field-secondary="setTagDetailFieldSecondary"
      />

      <OperatorAnalyticsDrawer
        :open="operatorDetail !== null"
        :detail="operatorDetail"
        :is-loading="operatorDetailLoading"
        :available-fields="filterOptions.fields || []"
        :field-selected="operatorDetailField"
        :field-secondary="operatorDetailFieldSecondary"
        :field-data="operatorDetailFieldData"
        :field-loading="operatorDetailFieldLoading"
        @close="closeOperatorDetail"
        @change-field="setOperatorDetailField"
        @change-field-secondary="setOperatorDetailFieldSecondary"
      />

      <ReportsModal
        :open="reportsModalOpen"
        :initial-filters="filters"
        :available-tags="filterOptions.tags || []"
        @close="closeReportsModal"
      />
    </div>
  </Layout>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Layout from '@/components/layout/vertical'
import appConfig from '@/app.config'

import DashboardHeader from './components/DashboardHeader.vue'
import InsightsTicker from './components/InsightsTicker.vue'
import HeroPulseCard from './components/HeroPulseCard.vue'
import KpiSparkRow from './components/KpiSparkRow.vue'
import RiverTimeline from './components/RiverTimeline.vue'
import TagFlowGrid from './components/TagFlowGrid.vue'
import TagHourHeatmap from './components/TagHourHeatmap.vue'
import FieldAnalysisCard from './components/FieldAnalysisCard.vue'
import OperatorPerformance from './components/OperatorPerformance.vue'
import AnomaliesPanel from './components/AnomaliesPanel.vue'
import TransitionFlow from './components/TransitionFlow.vue'
import TopLocations from './components/TopLocations.vue'
import LiveCheckinFeed from './components/LiveCheckinFeed.vue'
import TagAnalyticsDrawer from './components/TagAnalyticsDrawer.vue'
import OperatorAnalyticsDrawer from './components/OperatorAnalyticsDrawer.vue'
import ReportsModal from './components/ReportsModal.vue'

const PUSHER_CHANNEL = 'checkin-realtime'
const PUSHER_EVENT = 'CheckinScan'

const NS = 'checkinDashboard'

export default {
  name: 'checkin-dashboard',
  page: {
    title: 'Dashboard check-in',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    DashboardHeader,
    InsightsTicker,
    HeroPulseCard,
    KpiSparkRow,
    RiverTimeline,
    TagFlowGrid,
    TagHourHeatmap,
    FieldAnalysisCard,
    OperatorPerformance,
    AnomaliesPanel,
    TransitionFlow,
    TopLocations,
    LiveCheckinFeed,
    TagAnalyticsDrawer,
    OperatorAnalyticsDrawer,
    ReportsModal
  },
  data: () => ({
    liveChannel: null,
    pusherStateHandler: null,
    localKeyCounter: 0,
    reportsModalOpen: false
  }),
  computed: {
    ...mapState(NS, [
      'filters',
      'bucket',
      'overview',
      'timeline',
      'insights',
      'byTag',
      'byTagWindowMinutes',
      'heatmap',
      'fieldAnalysis',
      'operators',
      'anomalies',
      'funnel',
      'flow',
      'topLocations',
      'tagDetailField',
      'tagDetailFieldSecondary',
      'tagDetailFieldData',
      'tagDetailFieldLoading',
      'operatorDetail',
      'operatorDetailLoading',
      'operatorDetailField',
      'operatorDetailFieldSecondary',
      'operatorDetailFieldData',
      'operatorDetailFieldLoading',
      'liveFeed',
      'liveFeedConnected',
      'liveFeedPaused',
      'filterOptions',
      'isLoading',
      'isFirstLoad',
      'lastUpdate',
      'error',
      'tagDetail',
      'tagDetailLoading',
      'tagDetailBucket'
    ]),
    isFairMode () {
      return this.overview && this.overview.inside_now_mode === 'fair_dwell'
    },
    /**
     * Flag central — toda configuração de "exibir saídas" passa por aqui.
     * O backend manda no overview; default true se não estiver disponível.
     */
    showCheckout () {
      if (!this.overview) return true
      return this.overview.show_checkout !== false
    }
  },
  async mounted () {
    this.hydrate()
    await this.loadFilterOptions()
    await this.startPolling()
    this.connectLiveFeed()
  },
  beforeDestroy () {
    this.disconnectLiveFeed()
    this.stopPolling()
  },
  methods: {
    ...mapActions(NS, [
      'hydrate',
      'loadFilterOptions',
      'setFilters',
      'setBucket',
      'startPolling',
      'stopPolling',
      'refresh',
      'setPrimaryField',
      'setSecondaryField',
      'setTagWindow',
      'openTagDetail',
      'closeTagDetail',
      'setTagDetailBucket',
      'setTagDetailField',
      'setTagDetailFieldSecondary',
      'openOperatorDetail',
      'closeOperatorDetail',
      'setOperatorDetailField',
      'setOperatorDetailFieldSecondary'
    ]),
    handleChangeFilters (partial) {
      this.setFilters(partial)
    },
    handleBucketChange (bucket) {
      this.setBucket(bucket)
    },
    handleRefresh () {
      this.refresh()
    },
    openReportsModal () {
      this.reportsModalOpen = true
    },
    closeReportsModal () {
      this.reportsModalOpen = false
    },
    /**
     * Click num card de tag = filtra o dashboard por aquela tag.
     * Se já estiver filtrado por essa tag (e somente ela), desfaz o filtro
     * — comportamento de toggle pra fácil navegação.
     */
    /**
     * Click no card de tag agora abre drawer analítico (em vez de filtrar
     * o dashboard inteiro). Filtragem fica como ação secundária dentro
     * do drawer.
     */
    handleSelectTag (tag) {
      this.openTagDetail(Number(tag.tag_id))
    },
    handleSelectLocation (loc) {
      // TopLocations e TagFlowGrid abrem o mesmo drawer analítico
      this.openTagDetail(Number(loc.tag_id))
    },
    handleSelectOperator (op) {
      this.openOperatorDetail(Number(op.operator_id))
    },
    handleTagWindowChange (minutes) {
      this.setTagWindow(minutes)
    },
    handlePrimaryFieldChange (fieldRef) {
      this.setPrimaryField(fieldRef)
    },
    handleSecondaryFieldChange (fieldRef) {
      this.setSecondaryField(fieldRef)
    },

    /**
     * Conecta no Pusher e bind no evento CheckinScan. Tolerante a falhas:
     *  - Se $pusher não existe (instância sem Pusher), só ignora.
     *  - Se backend tem BROADCAST_DRIVER=null, frontend conecta mas nunca
     *    recebe eventos. UI fica "conectada" mas vazia — é OK.
     */
    connectLiveFeed () {
      if (!this.$pusher) return
      try {
        this.liveChannel = this.$pusher.subscribe(PUSHER_CHANNEL)
        this.liveChannel.bind(PUSHER_EVENT, (data) => {
          if (this.liveFeedPaused) return
          // Adiciona um _localKey pra garantir que o transition-group consiga
          // distinguir mesmo se o evento vier sem `id` (rare edge case).
          this.localKeyCounter++
          this.$store.commit(`${NS}/PREPEND_LIVE_EVENT`, {
            ...data,
            _localKey: 'lf' + this.localKeyCounter
          })
        })

        const sync = () => {
          this.$store.commit(
            `${NS}/SET_LIVE_CONNECTED`,
            this.$pusher.connection && this.$pusher.connection.state === 'connected'
          )
        }
        sync()
        this.pusherStateHandler = sync
        if (this.$pusher.connection && this.$pusher.connection.bind) {
          this.$pusher.connection.bind('state_change', this.pusherStateHandler)
        }
      } catch (e) {
        console.warn('[checkin-dashboard] Pusher subscribe failed:', e.message)
      }
    },

    disconnectLiveFeed () {
      if (!this.$pusher) return
      try {
        if (this.liveChannel) {
          this.liveChannel.unbind_all()
          this.$pusher.unsubscribe(PUSHER_CHANNEL)
          this.liveChannel = null
        }
        if (this.pusherStateHandler && this.$pusher.connection && this.$pusher.connection.unbind) {
          this.$pusher.connection.unbind('state_change', this.pusherStateHandler)
          this.pusherStateHandler = null
        }
      } catch (e) { /* ignore */ }
      this.$store.commit(`${NS}/SET_LIVE_CONNECTED`, false)
    },

    handleTogglePause () {
      this.$store.commit(`${NS}/SET_LIVE_PAUSED`, !this.liveFeedPaused)
    },

    handleClearLive () {
      this.$store.commit(`${NS}/CLEAR_LIVE_FEED`)
    }
  }
}
</script>

<style lang="scss" scoped>
.cd-page {
  padding: 8px 4px 24px;
}

.cd-spacer { height: 18px; }

/* Hero (check-ins realizados) + Live feed lado a lado (60/40) */
.cd-hero-row {
  display: grid;
  grid-template-columns: 6fr 4fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .cd-hero-row { grid-template-columns: 1fr; }
}

.cd-error {
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(244, 106, 106, 0.08);
  border: 1px solid rgba(244, 106, 106, 0.2);
  border-radius: 10px;
  color: #d04848;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;

  i { font-size: 18px; }
}

/* Skeleton enquanto carrega — shimmer com gradient pulsante. Diferente do
   spinner cheio que outras dashboards usam. */
.cd-skeleton {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.cd-sk-hero,
.cd-sk-card,
.cd-sk-chart,
.cd-sk-tag,
.cd-sk-heatmap,
.cd-sk-field,
.cd-sk-funnel,
.cd-sk-flow,
.cd-sk-ops-card {
  background: linear-gradient(90deg, #f0f1f6 0%, #f8f9fc 50%, #f0f1f6 100%);
  background-size: 200% 100%;
  border-radius: 5px;
  animation: cd-shimmer 1.6s ease-in-out infinite;
}

.cd-sk-hero { height: 180px; }

.cd-sk-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

.cd-sk-card { height: 110px; }
.cd-sk-chart { height: 360px; }

.cd-sk-tag-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.cd-sk-tag { height: 180px; }
.cd-sk-heatmap { height: 280px; }
.cd-sk-field { height: 420px; }
.cd-sk-funnel { height: 220px; }
.cd-sk-flow { height: 360px; }
.cd-sk-ops { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.cd-sk-ops-card { height: 360px; }

/* Grid 2-col pra OperatorPerformance + AnomaliesPanel */
.cd-ops-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@keyframes cd-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 1100px) {
  .cd-sk-row { grid-template-columns: repeat(2, 1fr); }
  .cd-sk-ops { grid-template-columns: 1fr; }
  .cd-ops-grid { grid-template-columns: 1fr; }
}
</style>
